import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CurrencyDollar, FloppyDisk } from "@phosphor-icons/react";

const toMinor = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
const toDollars = (m) => (m == null ? "" : (m / 100).toFixed(2));

function PriceCard({ testid, title, desc, value, onChange, onSave, busy }) {
  return (
    <Card className="p-6 rounded-xl border-border" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-accent grid place-items-center shrink-0">
          <CurrencyDollar className="w-5 h-5 text-primary" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">{title}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
          <div className="mt-4 flex items-end gap-3">
            <div className="space-y-1.5 flex-1 max-w-[220px]">
              <Label className="text-xs">Price per concurrent channel (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input data-testid={`${testid}-input`} type="number" min="0" step="0.01"
                  className="pl-7" value={value}
                  onChange={(e) => onChange(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <Button data-testid={`${testid}-save`} onClick={onSave} disabled={busy}
              className="rounded-lg">
              <FloppyDisk className="w-4 h-4 mr-1.5" weight="bold" /> Save
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function BillingPricing() {
  const [cust, setCust] = useState("");
  const [res, setRes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/pricing");
      setCust(toDollars(data.customer_price_minor));
      setRes(toDollars(data.reseller_price_minor));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (which) => {
    const raw = which === "customer" ? cust : res;
    const minor = toMinor(raw);
    if (minor == null || isNaN(minor) || minor < 0) {
      toast.error("Enter a valid non-negative price."); return;
    }
    setBusy(true);
    try {
      const body = which === "customer" ? { customer_price_minor: minor } : { reseller_price_minor: minor };
      const { data } = await api.put("/billing/pricing", body);
      setCust(toDollars(data.customer_price_minor));
      setRes(toDollars(data.reseller_price_minor));
      toast.success("Price saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="billing-pricing-page">
      <PageHeader title="Billing — Pricing"
        subtitle="Standard per-concurrent-channel prices. Accounts on 'Standard' pricing inherit these; changing a price never rewrites already-assigned accounts or past purchases." />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-4 max-w-3xl">
          <PriceCard testid="pricing-customer" title="Customer Concurrent Call Limit Price"
            desc="Price per additional concurrent call channel for customers."
            value={cust} onChange={setCust} onSave={() => save("customer")} busy={busy} />
          <PriceCard testid="pricing-reseller" title="Reseller Concurrent Call Limit Price"
            desc="Price per additional concurrent call channel for resellers."
            value={res} onChange={setRes} onSave={() => save("reseller")} busy={busy} />
        </div>
      )}
    </div>
  );
}
